/**
 * Crisis-schema — bommelding NCTV-protocol.
 * Plain TypeScript: geen zod, geen externe dependencies.
 *
 * Geporteerd van vivre-platform prepared-cp-kit structuur —
 * alleen de types en validatielogica, niet de Zod-schemas.
 */

// ============================================================
// Constante opties (als const-arrays voor rendering + validatie)
// ============================================================

export const CALLER_GENDER_OPTIONS = ["man", "vrouw", "onbekend"] as const;
export type CallerGender = (typeof CALLER_GENDER_OPTIONS)[number];

export const CALLER_VOICE_OPTIONS = [
  "rustig",
  "zenuwachtig",
  "boos",
  "huilend",
  "lachend",
  "dronken",
  "lispelend",
  "fluisterend",
  "schreeuwend",
  "anders",
] as const;
export type CallerVoice = (typeof CALLER_VOICE_OPTIONS)[number];

export const CALLER_SPEECH_OPTIONS = [
  "formeel",
  "informeel",
  "scheldend",
  "voorgelezen",
  "gehaast",
  "stotterend",
  "duidelijk",
  "onduidelijk",
] as const;
export type CallerSpeech = (typeof CALLER_SPEECH_OPTIONS)[number];

export const CALLER_ACCENT_OPTIONS = [
  "nederlandstalig",
  "vlaams",
  "buitenlands",
  "engels",
  "duits",
  "arabisch",
  "oost-europees",
  "onbekend",
] as const;
export type CallerAccent = (typeof CALLER_ACCENT_OPTIONS)[number];

export const BACKGROUND_SOUND_OPTIONS = [
  "verkeer",
  "muziek",
  "stemmen",
  "machines",
  "trein",
  "vliegtuig",
  "kinderen",
  "dieren",
  "wind",
  "stilte",
  "kantoor",
  "anders",
] as const;
export type BackgroundSound = (typeof BACKGROUND_SOUND_OPTIONS)[number];

// ============================================================
// Stap-types
// ============================================================

/** Stap 1 — letterlijk citaat van de beller + tijdstip. */
export interface Step1Data {
  threatText: string; // max 2000 tekens
  receivedAt: string; // "YYYY-MM-DDTHH:mm" (datetime-local inputwaarde)
}

/**
 * Stap 2 — NCTV sleutelvragen.
 * Alle velden optioneel: beller hangt soms vroegtijdig op.
 */
export interface Step2Data {
  whenExplode: string;  // Wanneer ontploft het?
  whereIs: string;      // Waar ligt het?
  howLooksLike: string; // Hoe ziet het eruit?
  whyDoingThis: string; // Waarom doet u dit?
  whoAreYou: string;    // Wie bent u?
  howDoYouKnow: string; // Hoe weet u dit?
}

/** Stap 3 — beller-kenmerken (geslacht, stem, spraak, accent). */
export interface Step3Data {
  gender: CallerGender | "";
  voice: CallerVoice[];
  speechStyle: CallerSpeech[];
  accent: CallerAccent | "";
}

/** Stap 4 — achtergrondgeluiden (12 checkboxes + vrije notitie). */
export interface Step4Data {
  sounds: BackgroundSound[];
  notes: string;
}

/** Stap 5 — melder-info (accountability). */
export interface Step5Data {
  operatorName: string; // Naam CP-operator
  callStart: string;    // "HH:mm"
  callEnd: string;      // "HH:mm"
}

/** Volledig rapport — alle 5 stappen gecombineerd. */
export interface BommeldingReport {
  step1: Step1Data;
  step2: Step2Data;
  step3: Step3Data;
  step4: Step4Data;
  step5: Step5Data;
}

// ============================================================
// Validatie (plain TS, geen externe validatiebibliotheek)
// ============================================================

export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

export function validateStep1(d: Step1Data): ValidationResult {
  const errors: Record<string, string> = {};
  if (!d.threatText.trim())
    errors.threatText = "Vul de letterlijke bedreigingstekst in";
  if (!d.receivedAt)
    errors.receivedAt = "Vul het tijdstip van ontvangst in";
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateStep2(d: Step2Data): ValidationResult {
  const errors: Record<string, string> = {};
  const filled = Object.values(d).filter((v) => v.trim().length > 0).length;
  if (filled === 0)
    errors._form = "Vul minimaal één antwoord in";
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateStep3(d: Step3Data): ValidationResult {
  const errors: Record<string, string> = {};
  if (!d.gender)
    errors.gender = "Selecteer het geslacht van de beller";
  return { valid: Object.keys(errors).length === 0, errors };
}

// Achtergrondgeluiden zijn volledig optioneel — altijd geldig.
export function validateStep4(_d: Step4Data): ValidationResult {
  return { valid: true, errors: {} };
}

export function validateStep5(d: Step5Data): ValidationResult {
  const errors: Record<string, string> = {};
  if (!d.operatorName.trim()) errors.operatorName = "Vul uw naam in";
  if (!d.callStart) errors.callStart = "Vul de starttijd van het gesprek in";
  if (!d.callEnd) errors.callEnd = "Vul de eindtijd van het gesprek in";
  return { valid: Object.keys(errors).length === 0, errors };
}

// ============================================================
// Serialisatie naar NL tekst (toelichting-veld in Firestore)
// ============================================================

function fmt(val: string | undefined | null, fallback = "—"): string {
  return val?.trim() || fallback;
}

/**
 * Zet een volledig rapport om naar leesbare NL-tekst.
 * Gebruikt als toelichting-veld bij het Firestore incident-document.
 */
export function serializeCrisisReport(r: BommeldingReport): string {
  // Tijdstip: behandel als lokale tijd (datetime-local invoerwaarde)
  const receivedFormatted = r.step1.receivedAt
    ? r.step1.receivedAt.replace("T", " ").slice(0, 16)
    : "—";

  const lines: string[] = [
    "BOMMELDING — NCTV-INTAKE",
    "",
    `Ontvangen:       ${receivedFormatted}`,
    `CP-operator:     ${fmt(r.step5.operatorName)}`,
    `Gesprekstijd:    ${fmt(r.step5.callStart)} – ${fmt(r.step5.callEnd)}`,
    "",
    "=== STAP 1: BEDREIGINGSTEKST ===",
    fmt(r.step1.threatText, "(niet ingevuld)"),
    "",
    "=== STAP 2: SLEUTELVRAGEN ===",
    `Wanneer ontploft het?   ${fmt(r.step2.whenExplode)}`,
    `Waar ligt het?          ${fmt(r.step2.whereIs)}`,
    `Hoe ziet het eruit?     ${fmt(r.step2.howLooksLike)}`,
    `Waarom doet u dit?      ${fmt(r.step2.whyDoingThis)}`,
    `Wie bent u?             ${fmt(r.step2.whoAreYou)}`,
    `Hoe weet u dit?         ${fmt(r.step2.howDoYouKnow)}`,
    "",
    "=== STAP 3: BELLER-KENMERKEN ===",
    `Geslacht: ${fmt(r.step3.gender)}`,
    `Stem:     ${r.step3.voice.length > 0 ? r.step3.voice.join(", ") : "—"}`,
    `Spraak:   ${r.step3.speechStyle.length > 0 ? r.step3.speechStyle.join(", ") : "—"}`,
    `Accent:   ${fmt(r.step3.accent)}`,
    "",
    "=== STAP 4: ACHTERGRONDGELUIDEN ===",
    r.step4.sounds.length > 0
      ? r.step4.sounds.join(", ")
      : "(geen aangevinkt)",
    ...(r.step4.notes.trim()
      ? [`Notities: ${r.step4.notes.trim()}`]
      : []),
    "",
    "=== STAP 5: MELDER ===",
    `Naam:           ${fmt(r.step5.operatorName)}`,
    `Start gesprek:  ${fmt(r.step5.callStart)}`,
    `Einde gesprek:  ${fmt(r.step5.callEnd)}`,
  ];

  return lines.join("\n");
}
